import {
  detectSiteMode,
  siteForHost,
  targetOrigin,
  type SiteMode,
} from './detect-site-mode';
import { mapRoute, responsivePublicMobileTarget } from './route-map';

interface TicketResponse {
  ticket: string;
  exchange_url: string;
  expires_in: number;
}

interface ExchangeResponse {
  target_route: string;
}

function unwrapData<T>(value: T | { data: T }): T {
  if (
    value &&
    typeof value === 'object' &&
    'data' in value &&
    (value as { data?: T }).data
  ) {
    return (value as { data: T }).data;
  }
  return value as T;
}

async function postJSON<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`site switch request failed: ${response.status}`);
  }
  return unwrapData((await response.json()) as T | { data: T });
}

async function exchangeTicket(): Promise<boolean> {
  if (window.location.pathname !== '/auth/switch') {
    return false;
  }
  const ticket = new URLSearchParams(window.location.search).get('ticket');
  if (!ticket) {
    window.history.replaceState(window.history.state, '', '/login?site_switch_error=missing');
    return false;
  }
  try {
    const exchanged = await postJSON<ExchangeResponse>(
      '/auth/site-switch/exchange',
      { ticket },
    );
    window.location.replace(exchanged.target_route);
    return true;
  } catch {
    window.history.replaceState(window.history.state, '', '/login?site_switch_error=invalid');
    return false;
  }
}

async function redirectToSite(currentSite: SiteMode): Promise<void> {
  const targetSite: SiteMode = currentSite === 'desktop' ? 'mobile' : 'desktop';
  const route = mapRoute(currentSite, window.location);
  const origin = targetOrigin(targetSite, window.location);

  try {
    const ticketResponse = await postJSON<TicketResponse>(
      '/auth/site-switch/tickets',
      {
        target_site: targetSite,
        target_route: route,
      },
    );
    if (!ticketResponse.ticket || ticketResponse.ticket.length !== 64) {
      throw new Error('invalid site switch ticket response');
    }
    const exchangeURL = new URL('/auth/switch', origin);
    exchangeURL.searchParams.set('ticket', ticketResponse.ticket);
    window.location.replace(exchangeURL.toString());
  } catch {
    window.location.replace(`${origin}${route}`);
  }
}

/**
 * Runs before the React application is dynamically imported. Returning false
 * means navigation has started and the caller must not download/start the
 * site's business bundle.
 */
export async function prepareSite(expectedSite: SiteMode): Promise<boolean> {
  const currentSite = siteForHost(window.location.hostname, window.location.port);
  if (!currentSite || currentSite !== expectedSite) {
    return true;
  }

  if (await exchangeTicket()) {
    return false;
  }

  const publicMobileTarget = responsivePublicMobileTarget(
    currentSite,
    window.location,
  );
  if (publicMobileTarget) {
    if (currentSite === 'mobile') {
      return true;
    }
    window.location.replace(
      `${targetOrigin('mobile', window.location)}${publicMobileTarget}`,
    );
    return false;
  }

  const browserMode = detectSiteMode(window.navigator);
  if (browserMode !== currentSite) {
    await redirectToSite(currentSite);
    return false;
  }
  return true;
}
